import {
  Component,
  ElementRef,
  ViewChild,
  signal,
  computed,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { AssetsService } from '../../services/asset.service';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SpinnerComponent } from '../../components/spinner/spinner';
import { NavButtonComponent } from '../../components/nav-button/nav-button';
import { LegalLocalizationService } from '../../services/legal-localization-service';

import { UiService } from '../../services/ui.service';
import { I18nService } from '../../services/i18n.service';
import { ICountry } from '../../models/object.model';
import { CommonFormComponent, FormField } from '../../components/common-form/common-form';
import { CustomValidators } from '../../utilities/customer_validators';
import { AuthenticationService } from '../../services/authentication.service';
import { FormKey } from '../../enums/i18.enum';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { LP_UI_CONFIG } from '../../core/tokens';
import { logError } from '../../utilities/global.util';
@Component({
  selector: 'app-signup',
  imports: [
    CommonModule,
    RouterModule,
    NzImageModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    SpinnerComponent,
    NavButtonComponent,
    CommonFormComponent,
  ],
  templateUrl: './signup.html',
  styleUrls: ['./signup.scss'],
})
export class Signup implements OnInit {
  // Inject services using inject()
  private router = inject(Router);
  public assetService = inject(AssetsService);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  public route = inject(ActivatedRoute);
  private lls = inject(LegalLocalizationService);
  private authenticationService = inject(AuthenticationService);
  public readonly cfg = inject(LP_UI_CONFIG);
  public signUpForm = new CommonFormComponent();
  @ViewChild('emailInputRef') emailInputRef!: ElementRef;

  // Use signals for state management
  isLoading = signal(false);
  emailAlreadyRegistered = signal(false);
  error = signal(false);
  showAlert = signal(false);
  optional = signal(false);
  checkedTerms = signal(false);
  checkedServiceConditions = false;
  checkedDataProtection = signal(false);
  private queryParams = toSignal(this.route.queryParams);
  country = computed(() => this.queryParams()?.['country'] ?? null);
  countryArray = signal<ICountry[]>([]);
  countryList = signal<ICountry[]>([]);
  countrySelected = signal<any>(null);
  showNotUsPage = signal(true);

  isMobile = this.ui.IsMobile;
  isTablet = this.ui.IsTablet;
  isDesktop = this.ui.IsDesktop;
  I18N = computed(() => this.i18n);
  signUpFormConfig: FormField[] = [
    {
      controlName: FormKey.Name,
      label: this.i18n.langString(FormKey.Name),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Name),
      initialValue: GLOBAL_CONSTANT.EMPTY_STRING,
      validators: [...CustomValidators.requiredMinLength(3)],
    },
    {
      controlName: FormKey.Surname,
      label: this.i18n.langString(FormKey.Surname),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Surname),
      initialValue: GLOBAL_CONSTANT.EMPTY_STRING,
      validators: [...CustomValidators.requiredMinLength(3)],
    },
    {
      controlName: FormKey.Email,
      label: this.i18n.langString(FormKey.Email),
      controlType: FormKey.Text,
      placeholder: FormKey.EmailPlaceholder,
      initialValue: GLOBAL_CONSTANT.EMPTY_STRING,
      validators: [...CustomValidators.email],
    },
    {
      controlName: FormKey.Title,
      label: this.i18n.langString(FormKey.Designation),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.Designation),
      initialValue: null,
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Country,
      label: this.i18n.langString(FormKey.Country),
      controlType: FormKey.Select,
      placeholder: this.i18n.langString(FormKey.SelectCountry),
      initialValue: FormKey.InitialUser,
      validators: [Validators.required],
    },
    {
      controlName: FormKey.CustomerEntity,
      label: this.i18n.langString(FormKey.CustomerEntity),
      controlType: FormKey.Text,
      placeholder: this.i18n.langString(FormKey.CustomerEntity),
      initialValue: null,
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Password,
      label: this.i18n.langString(FormKey.Password),
      controlType: FormKey.Password,
      placeholder: FormKey.PasswordMask,
      initialValue: null,
      validators: [...CustomValidators.strongPassword],
    },
    {
      controlName: FormKey.ConfirmPassword,
      label: this.i18n.langString(FormKey.PwValidation6),
      controlType: FormKey.Password,
      placeholder: FormKey.PasswordMask,
      initialValue: null,
      validators: [Validators.required],
    },
    {
      controlName: FormKey.Checkbox,
      label: ' ',
      controlType: FormKey.Checkbox,
      placeholder: GLOBAL_CONSTANT.EMPTY_STRING,
      initialValue: null,
    },
    {
      controlName: FormKey.CaptchaResponse,
      label: this.cfg.enableCaptcha ? FormKey.Captcha : GLOBAL_CONSTANT.EMPTY_STRING,
      controlType: FormKey.Captcha,
      placeholder: '',
      initialValue: null,
      validators: this.cfg.enableCaptcha ? [Validators.required] : [],
    },
  ];

  private showNotUsPageEffect = effect(() => {
    const currentCountry = this.country();
    this.showNotUsPage.set(currentCountry !== 'us');
  });
  sourceCountries: any;
  filteredCountries: any;

  ngOnInit() {
    this.assetService.getCountryList().then((countryList) => {
      this.sourceCountries = countryList;
      const isUSSelected = this.country() === 'us';
      this.filteredCountries = this.sourceCountries.filter((item: { name: any }) => {
        try {
          const parsed = JSON.parse(item.name.trim());
          const countryName = parsed.en;
          if (isUSSelected) {
            // US selected → show only US and Canada
            return countryName === 'United States' || countryName === 'Canada';
          } else {
            // Not US → exclude US and Canada
            return countryName !== 'United States' && countryName !== 'Canada';
          }
        } catch (e) {
          logError(e);
          return false;
        }
      });      
      this.countryArray.set(this.filteredCountries);
      this.sortCountries();
    });
  }

  goToLoginPage(): void {
    const isUS = this.country() === 'us';
    this.router.navigate(['login'], {
      queryParams: isUS ? { country: 'us' } : undefined,
    });
  }

  createUser(formData: any) {
    this.error.set(false);
    this.emailAlreadyRegistered.set(false);
    this.isLoading.set(true);
    this.authenticationService.signup(formData, this.i18n.languageSignal()).subscribe(
      (data) => {
        if (data) {
          this.isLoading.set(false);
          this.router.navigate(['login', 'signed-up']);
        } else {
          this.isLoading.set(false);
          this.error.set(true);
        }
      },
      (error) => {
        this.isLoading.set(false);
        if (error.status === 409) {
          this.emailAlreadyRegistered.set(true);
        } else {
          this.error.set(true);
        }
      }
    );
  }

  resetEmailAlreadyRegisterdcheck() {
    this.emailAlreadyRegistered.set(false);
  }

  toTermsOfUse() {
    this.router.navigate([
      // this.country() === 'us' ? 'us/terms-of-use' : '/terms-of-use',
    ]);
  }

  toDataProtectionDeclaration() {
    this.router.navigate([
      // this.country() === 'us' ? 'us/data-protection' : '/data-protection',
    ]);
  }

  toServiceConditions() {
    // this.router.navigate(['/service-conditions']);
  }

  sortCountries() {
    this.countryArray.set(
      this.countryArray().sort((a, b) => {
        if (this.i18n.parse(a.name).toUpperCase() > this.i18n.parse(b.name).toUpperCase()) return 1;
        if (this.i18n.parse(a.name).toUpperCase() < this.i18n.parse(b.name).toUpperCase())
          return -1;
        return 0;
      })
    );
  }
}
